import React, { useState, KeyboardEvent, useRef, useEffect } from 'react';
import Icon from '@mdi/react';
import { mdiStar, mdiSend } from '@mdi/js';
import styles from './chatInput.module.scss';
import { images } from '../../../../constants/images';

interface ChatInputProps {
    onSendMessage: (message: string) => void;
    disabled: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({ onSendMessage, disabled }) => {
    const [message, setMessage] = useState('');
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const shouldFocusRef = useRef(false);

    const handleSend = () => {
        if (message.trim() && !disabled) {
            onSendMessage(message.trim());
            setMessage('');
            if (textareaRef.current) {
                textareaRef.current.style.height = 'auto';
            }
            shouldFocusRef.current = true;
        }
    };

    const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;

        // Si es el primer carácter y es una letra, capitalizarla
        if (value.length === 1 && /[a-záéíóúñ]/i.test(value)) {
            setMessage(value.charAt(0).toUpperCase());
        } else {
            setMessage(value);
        }
    };

    // Auto-resize textarea
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    }, [message]);

    // Mantener el foco después de enviar mensaje
    useEffect(() => {
        if (shouldFocusRef.current && textareaRef.current) {
            textareaRef.current.focus();
            shouldFocusRef.current = false;
        }
    }, [message]);

    // Auto-focus al montar el componente
    useEffect(() => {
        if (textareaRef.current && !disabled) {
            textareaRef.current.focus();
        }
    }, [disabled]);

    return (
        <>
            <div className={styles.inputWrapperDisclaimer}>
                <p className={styles.disclaimer}>
                    La IA puede cometer errores, considera verificar la información importante
                </p>
            </div>
            <div className={styles.inputWrapper}>

                <div className={styles.inputContainer}>
                    <div className={styles.inputField}>
                        <div className={styles.starIcon}>
                            <img src={images.chatSparkle} alt="Sparkle" className={styles.sparkle} />
                        </div>
                        <textarea
                            ref={textareaRef}
                            className={styles.input}
                            placeholder="¿Qué quieres saber de Linze?"
                            value={message}
                            onChange={handleChange}
                            onKeyPress={handleKeyPress}
                            disabled={disabled}
                            rows={1}
                            autoCapitalize="sentences"
                        />
                    </div>
                    <button
                        className={styles.sendButton}
                        onClick={handleSend}
                        disabled={disabled || !message.trim()}
                        aria-label="Enviar mensaje"
                    >
                        <Icon path={mdiSend} size={0.9} />
                    </button>
                </div>
            </div>
        </>
    );
};

export default ChatInput;
